import React, { useState, useRef, useEffect } from "react";
import DropDown from "../DropDown";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ConfirmModal from "../Modals/ConfirmModal";

const IndicadoresForm = ({
  periodos,
  periodoId,
  setPeriodoId,
  asignaturasDelSalon,
  indicadores,
  indicadoresGuardados,
  errores = [],
  handleChange,
  handleSubmit,
  handleDelete,
  salonId,
}) => {
  
  const hayIndicadoresGuardados = Array.isArray(indicadoresGuardados) && indicadoresGuardados.length > 0;
  const quillRefs = useRef({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ✅ CARGAR CONTENIDO EN QUILL cuando cambien los indicadores guardados
  useEffect(() => {
    if (indicadoresGuardados && indicadoresGuardados.length > 0) {
      indicadoresGuardados.forEach((ind) => {
        const ref = quillRefs.current[ind.asignatura_salon_id]; // 🔥 CAMBIO: usar asignatura_salon_id
        if (ref && ref.getEditor) {
          const editor = ref.getEditor();
          const contenido = ind.descripcion || "";
          editor.clipboard.dangerouslyPasteHTML(contenido);
        }
      });
    } else {
      Object.values(quillRefs.current).forEach((ref) => {
        if (ref && ref.getEditor) {
          const editor = ref.getEditor();
          editor.setText("");
        }
      });
    }
  }, [indicadoresGuardados]);

  const handleConfirmDelete = async () => {
    try {
      setIsLoading(true);
      await handleDelete(periodoId, salonId);
      setShowDeleteModal(false);
      
      Object.values(quillRefs.current).forEach((ref) => {
        if (ref && ref.getEditor) {
          const editor = ref.getEditor();
          editor.setText("");
        }
      });
    } catch (err) {
      console.error("Error al eliminar:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // 🔥 VALIDACIÓN: Si no hay asignaturas del salón
  if (!asignaturasDelSalon || asignaturasDelSalon.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <div className="mb-6">
          <label className="block mb-2 text-lg font-semibold text-gray-700">Seleccione Periodo</label>
          <DropDown
            value={periodoId}
            onChange={(e) => setPeriodoId(e.target.value)}
            options={periodos?.map((p) => ({ value: p.id, label: p.nombre })) || []}
            placeholder="Seleccione..."
            className="w-full"
          />
        </div>
        <div className="text-center text-gray-500 py-8">
          {periodoId 
            ? "No hay asignaturas asignadas a este salón. Por favor, configure las asignaturas en el módulo de Asignaciones."
            : "Seleccione un periodo para continuar."
          }
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="mb-6">
        <label className="block mb-2 text-lg font-semibold text-gray-700">Seleccione Periodo</label>
        <DropDown
          value={periodoId}
          onChange={(e) => setPeriodoId(e.target.value)}
          options={periodos?.map((p) => ({ value: p.id, label: p.nombre })) || []} // 🔥 Validación
          placeholder="Seleccione..."
          className="w-full"
        />
      </div>

      <table className="w-full border-collapse border border-gray-200 rounded-lg overflow-hidden shadow-md">
        <thead className="bg-blue-900 text-white">
          <tr>
            <th className="px-6 py-3 text-left text-lg font-medium border-b border-gray-200" style={{ width: "46%" }}>
              Asignatura
            </th>
            <th className="px-6 py-3 text-left text-lg font-medium border-b border-gray-200" style={{ width: "54%" }}>
              Descripción
            </th>
          </tr>
        </thead>
        <tbody>
          {asignaturasDelSalon.map((asigSalon, index) => (
            <tr
              key={asigSalon.asignatura_salon_id}
              className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
            >
              <td className="px-6 py-4 text-sm font-medium border-b border-gray-dark">
                {asigSalon.nombre}
              </td>
              <td className="px-6 py-4 border-b border-gray-dark">
                <ReactQuill
                  ref={(el) => (quillRefs.current[asigSalon.asignatura_salon_id] = el)}
                  onChange={(value) => handleChange(asigSalon.asignatura_salon_id, value)}
                  theme="snow"
                  style={{ backgroundColor: "white", wordBreak: "break-word" }}
                  modules={{
                    toolbar: [
                      [{ 'list': 'bullet' }],
                      ['bold', 'italic', 'underline'],
                      ['clean']
                    ]
                  }}
                  formats={['bold', 'italic', 'underline', 'list', 'bullet']}
                  className={`bg-white border w-full min-h-[120px] max-w-full overflow-hidden border-black rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errores.includes(asigSalon.asignatura_salon_id) ? "border-red-500" : "border-gray-300"
                  }`}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-6 flex justify-start space-x-4">
        <button
          onClick={handleSubmit}
          disabled={!periodoId}
          className="bg-main-blue text-white cursor-pointer px-4 py-2 rounded-lg shadow hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {hayIndicadoresGuardados ? "Actualizar Indicadores" : "Guardar Indicadores"}
        </button>

        {hayIndicadoresGuardados && (
          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-500 text-white cursor-pointer px-4 py-2 rounded-lg shadow hover:bg-red-600"
          >
            Eliminar Indicadores
          </button>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Confirmar Eliminación"
        message="¿Estás seguro de que deseas eliminar los indicadores del período seleccionado? Esta acción no se puede deshacer."
        isLoading={isLoading}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default IndicadoresForm;